// Core
import React from "react"
import { Link } from "gatsby"
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import Img from "gatsby-image"

import SplitText from "./split-text/split-text"
import YoutubeComponent from "./youtube/youtube"

// Headings
const Heading1 = ({ children }) => <h1>{children}</h1>,
  Heading2 = ({ children }) => <h2>{children}</h2>,
  Heading3 = ({ children }) => <h3>{children}</h3>,
  Heading4 = ({ children }) => <h4>{children}</h4>,
  Heading5 = ({ children }) => <h5>{children}</h5>,
  Heading6 = ({ children }) => <h6>{children}</h6>

// Paragraph
const Text = ({ children }) => <p>{children}</p>
const Bold = ({ children }) => <strong>{children}</strong>

// Links
const ExternalLink = ({ uri, children }) => (
  <a href={uri} target={`_blank`}>
    {children}
  </a>
)
const InlineLink = ({ fields, children }) => (
  <Link
    to={fields.slug["en-US"]}
    aria-label={`"Go to ${fields.title["en-US"]} page"`}
  >
    {children}
  </Link>
)

const options = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
    superscript: text => <sup>{text}</sup>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return <Text>{children}</Text>
    },
    [BLOCKS.HEADING_1]: (node, children) => <Heading1>{children}</Heading1>,
    [BLOCKS.HEADING_2]: (node, children) => <Heading2>{children}</Heading2>,
    [BLOCKS.HEADING_3]: (node, children) => <Heading3>{children}</Heading3>,
    [BLOCKS.HEADING_4]: (node, children) => <Heading4>{children}</Heading4>,
    [BLOCKS.HEADING_5]: (node, children) => <Heading5>{children}</Heading5>,
    [BLOCKS.HEADING_6]: (node, children) => <Heading6>{children}</Heading6>,
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      switch (node.data.target.__typename) {
        case "ContentfulVideo":
          return (
            <YoutubeComponent
              url={node.data.target.youtubeURL}
              title={node.data.target.title}
            />
          )
        default:
          return ``
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: node => {
      return (
        <Img
          fluid={node.data.target.fluid}
          alt={node.data.target.description}
          objectFit="cover"
          objectPosition="center"
          imgStyle={{ objectPosition: "center" }}
          style={{ height: "auto" }}
        />
      )
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      return <InlineLink {...node.data.target}>{children}</InlineLink>
    },
    [INLINES.HYPERLINK]: (node, children) => {
      return <ExternalLink {...node.data}>{children}</ExternalLink>
    },
  },
  renderText: text => {
    return text.split("\n").reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
}

const splitOptions = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <SplitText classString={`copy`}>
        <h1>{children}</h1>
      </SplitText>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <SplitText classString={`copy`}>
        <h2>{children}</h2>
      </SplitText>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <SplitText classString={`copy`}>
        <h3>{children}</h3>
      </SplitText>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <SplitText classString={`copy`}>
        <h4>{children}</h4>
      </SplitText>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return <SplitText classString={`copy copy--m`}>{children}</SplitText>
    },
  },
  renderText: text => {
    return text.split("\n").reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
}
const splitSmallOptions = {
  renderMark: {
    [MARKS.BOLD]: text => <Bold>{text}</Bold>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <SplitText classString={`copy`}>
        <h1>{children}</h1>
      </SplitText>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <SplitText classString={`copy`}>
        <h2>{children}</h2>
      </SplitText>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <SplitText classString={`copy`}>
        <h3>{children}</h3>
      </SplitText>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <SplitText classString={`copy`}>
        <h4>{children}</h4>
      </SplitText>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return <SplitText classString={`copy copy--s`}>{children}</SplitText>
    },
  },
  renderText: text => {
    return text.split("\n").reduce((children, textSegment, index) => {
      return [...children, index > 0 && <br key={index} />, textSegment]
    }, [])
  },
}

export default ({ content, small, split }) =>
  renderRichText(
    content,
    split && small ? splitSmallOptions : split ? splitOptions : options
  )
